import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import moment from 'moment';

import {
  AlertModalComponent,
  modalTypes
} from '@components/modals/alert-modal/alert-modal.component';
import { SubscriptionHandler } from '@components/subscriptionHandler';
import { constants } from '@constants/constants';
import { getLineChartData, getLineChartLabels } from '@extensions/extensions';
import { MyRateModel } from '@extensions/user-data-extensions';
import { DropDownFormControl } from '@form/dropdown-form-control/dropdown-form-control';
import { EuroPipe } from '@pipes/euro.pipe';
import { LabelTextPipe } from '@pipes/label-text.pipe';
import { ProgressBarService } from '@services/progress-bar.service';
import { GetTarifsPrevoyanceGPAResponse } from '@webapi/MIF.Subscription.Parrot';
import { Profile } from '@webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@webapi/services/customer.service';
import { WsPrevoyanceGPAService } from '@webapi/services/ws-pevoyance-gpa';
import {
  GpaCapitalData,
  WsReferentielMetierService
} from '@webapi/services/ws-referentiel-metier.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { pushGtmTag } from '@shared/extensions/gtm.extentions';
import { ProfileStepCode } from '@shared/models/profile-step-code';

@Component({
  standalone: false,
  selector: 'app-my-rate',
  templateUrl: './my-rate.component.html',
  styleUrls: ['./my-rate.component.scss']
})
export class MyRateComponent implements OnInit, OnDestroy {
  clientProfile: Profile;
  clientProfileForViewOffer: Profile;
  constants: any = constants;
  offersCount: number;
  gpaCapitalsControl: DropDownFormControl;
  pivotAge: number;
  gpaCapitalLimits: GpaCapitalData;
  clientAge: number;
  clientAgeAtTheEndOfYear: number;
  ageLessOrEqualPivot: boolean;
  x2x3Model: boolean;
  planDetails: GetTarifsPrevoyanceGPAResponse;
  isShowDiagram: boolean;
  public chartData?: ChartData<'line', number[], string>;
  public chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Cotisations mensuelles',
        position: 'left'
      },
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (ctx: TooltipItem<'line'>[]) => {
            // ctx[0].label is the string from `labels[]`
            const ageLabel = ctx[0].label as string;
            return `${ageLabel} ans`;
          },
          label: (ctx: TooltipItem<'line'>) => {
            const y = ctx.parsed.y as number;
            return `${y}€`.replace('.', ',');
          },
          afterLabel: (ctx: TooltipItem<'line'>) => {
            const ageNum = parseInt(ctx.parsed.x as unknown as string, 10);
            if (ageNum >= 71) {
              return (
                `À partir de 71 ans, votre cotisation reste fixe pour les années restantes\n` +
                `et le montant de votre capital garanti diminue suivant un barème\n` +
                `annexé à la note d’information.`
              );
            }
            return '';
          }
        }
      }
    },
    layout: {
      padding: { top: 10, right: 50, bottom: 20, left: 10 }
    },
    scales: {
      x: {
        type: 'category', // ← MUST be 'category' so Chart.js uses your labels[]
        ticks: {
          callback: function (_value: unknown, index: number) {
            const allLabels =
              (this.chart.data.labels as string[] | undefined) || [];
            const label = allLabels[index] ?? '';
            return label !== 'Age' ? `${label} ans` : label;
          }
        },
        grid: {
          borderDashPattern: [6, 2] // use the v4 name
        } as any
      },
      y: {
        ticks: {
          callback: (val: string | number) => {
            return `${val}€`.replace('.', ',');
          }
        },
        grid: {
          borderDashPattern: [6, 2]
        } as any
      }
    },
    elements: {
      line: {
        tension: 0
      }
    }
  };
  public subscriptionHandler: SubscriptionHandler = new SubscriptionHandler();

  constructor(
    private router: Router,
    private customerService: CustomerService,
    private wsReferentielMetierService: WsReferentielMetierService,
    private progressBarService: ProgressBarService,
    private euroPipe: EuroPipe,
    private bsModalService: BsModalService,
    private wsPrevoyanceGPAService: WsPrevoyanceGPAService,
    private labelTextPipe: LabelTextPipe
  ) {}

  ngOnDestroy(): void {
    this.subscriptionHandler.unsubscribeAll();
  }

  form: FormGroup = new FormGroup({
    gpaCapitals: new DropDownFormControl(
      true,
      false,
      {
        collection: [],
        valuePropertyName: 'label',
        keyPropertyName: 'value',
        disableChooseOption: true
      },
      {
        hideErrorMark: true,
        requiredErrorMessage: ''
      }
    )
  });

  async ngOnInit(): Promise<void> {
    this.progressBarService.sendStep(
      constants.steps.myContracts.myRate.initialStep
    );
    await this.fetchInitData();
    this.initControls();
    this.restoreAnswersOnForm();
  }

  restoreAnswersOnForm() {
    this.initGpaCapitalItems();
    const model = this.customerService.getMyRateModel();
    if (model.guaranteedCapitalCode) {
      if (model.isMultipliedCoverage) {
        this.x2x3Model = model.isMultipliedCoverage;
      }

      this.form.get('gpaCapitals').patchValue(model.guaranteedCapitalCode);
      this.recalculateRate();
    }
  }

  onOffersCountChanged(count: number): void {
    this.offersCount = count > 0 ? count - 1 : null;
  }

  async fetchInitData() {
    this.clientProfile = await this.customerService.getUserData();
    this.clientAge = moment().diff(
      this.clientProfile.person.dateOfBirth,
      'years',
      false
    );
    this.clientAgeAtTheEndOfYear = moment()
      .set('month', 11)
      .set('date', 31)
      .diff(this.clientProfile.person.dateOfBirth, 'years', false);
    this.pivotAge = await this.wsReferentielMetierService.getPivotAge();
    this.ageLessOrEqualPivot = this.clientAgeAtTheEndOfYear <= this.pivotAge;
    if (!this.ageLessOrEqualPivot) {
      this.x2x3Model = true;
    }
    this.gpaCapitalLimits =
      await this.wsReferentielMetierService.getGpaCapitalLimits();
  }

  initControls() {
    this.gpaCapitalsControl = this.form.get(
      'gpaCapitals'
    ) as DropDownFormControl;

    this.subscriptionHandler.subscriptions = this.form.valueChanges.subscribe(
      () => {
        this.recalculateClientProfileForViewOffer();
      }
    );
  }

  initGpaCapitalItems() {
    let value = this.gpaCapitalLimits.min;
    let id = 1;
    const items = [];
    const max = this.ageLessOrEqualPivot
      ? this.gpaCapitalLimits.max
      : this.gpaCapitalLimits.maxX2X3;

    while (value <= max) {
      items.push({
        id: id,
        value: value,
        label: this.euroPipe.transform(value, true)
      });
      value += this.gpaCapitalLimits.step;
      id += 1;
    }

    this.gpaCapitalsControl.setCollection(items);
  }

  recalculateClientProfileForViewOffer(): void {
    const model = new MyRateModel();

    model.guaranteedCapitalCode = this.form.get('gpaCapitals').value;
    model.isMultipliedCoverage = this.x2x3Model;
    model.monthlyPayment = this.planDetails?.cotisation[0]?.mntCotisation;

    this.clientProfileForViewOffer = Object.assign(
      new Profile(),
      this.customerService.updateMyRateModelWithoutSaving(
        this.clientProfile,
        model
      )
    );
  }

  async recalculateRate() {
    if (this.gpaCapitalsControl.value > this.gpaCapitalLimits.maxX2X3) {
      this.x2x3Model = false;
    }

    if (
      !this.gpaCapitalsControl.collection.find(
        (item: any) => item.value === this.gpaCapitalsControl.value
      )
    ) {
      this.gpaCapitalsControl.patchValue(null);
      this.x2x3Model = true;
      return;
    }

    const res = (await this.wsPrevoyanceGPAService.getTarifsPrevoyanceGPA(
      this.clientAgeAtTheEndOfYear,
      this.x2x3Model,
      this.gpaCapitalsControl.value
    )) as GetTarifsPrevoyanceGPAResponse;

    if (res.erreurs?.length > 0) {
      this.bsModalService.show(AlertModalComponent, {
        initialState: {
          type: modalTypes.error,
          title: res.erreurs[0].codeErreur + ' ' + res.erreurs[0].libelleErreur
        }
      });
    } else {
      this.processResponse(res);
    }

    this.recalculateClientProfileForViewOffer();
  }

  processResponse(res: GetTarifsPrevoyanceGPAResponse) {
    this.planDetails = res;
    this.buildChart(res);
  }

  buildChart(res: GetTarifsPrevoyanceGPAResponse) {
    const labels = getLineChartLabels(res);
    const data = getLineChartData(res);

    this.chartData = {
      labels,
      datasets: [
        {
          data,
          label: '€',
          borderColor: '#189bd5',
          backgroundColor: 'transparent'
        }
      ]
    };
  }

  x2x3ReadMoreClick(): void {
    const guaranteedCapitalAmount = this.euroPipe.transform(150000, true);
    const text = this.labelTextPipe.transform(
      'CAPITAL.ATDNX2X3GuaranteeLink',
      guaranteedCapitalAmount
    );
    this.bsModalService.show(AlertModalComponent, {
      initialState: {
        type: modalTypes.info,
        body: text
      }
    });
  }

  inclusLesServicesClick(): void {
    const text = this.labelTextPipe.transform('CAPITAL.ATDNServicesLinkText');
    this.bsModalService.show(AlertModalComponent, {
      initialState: {
        type: modalTypes.info,
        body: text
      }
    });
  }

  showDiagram(): void {
    this.isShowDiagram = true;
  }

  hideDiagram(): void {
    this.isShowDiagram = false;
  }

  get isCapitalLessOrEqualMaxX2X3(): boolean {
    return (
      this.gpaCapitalsControl?.value &&
      this.gpaCapitalsControl.value <= this.gpaCapitalLimits.maxX2X3
    );
  }

  get capitalChoisedOrMaxX2X3(): number {
    if (this.gpaCapitalsControl?.value) {
      return this.isCapitalLessOrEqualMaxX2X3
        ? this.gpaCapitalsControl?.value
        : this.gpaCapitalLimits.maxX2X3;
    } else {
      return this.gpaCapitalLimits.maxX2X3;
    }
  }

  async submit() {
    const model = new MyRateModel();

    model.guaranteedCapitalCode = this.form.get('gpaCapitals').value;
    model.isMultipliedCoverage = this.x2x3Model;
    model.monthlyPayment = this.planDetails?.cotisation[0]?.mntCotisation;

    const userId = await this.customerService.updateMyRateModel(model);

    pushGtmTag(ProfileStepCode.MyRate);

    this.router.navigate(['my-beneficiaries'], { queryParams: { userId } });
  }
}
